import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  User, CalendarDays, Users, MapPin, Navigation, LayoutDashboard,
  Sparkles, Globe, Receipt, Music, Music2,
  PenLine, AtSign, Plus, Clipboard,
  FileText, UserPlus,
  Building2, Mic, Home,
  Clock, Map, CreditCard, Share2,
  GripVertical, EyeOff,
  Hash, Bell, Download,
  Check, ChevronDown, ChevronUp,
  ChevronRight, ArrowLeft, ArrowRight,
} from "lucide-react";
import { apiClient, useAuth } from "@/App";
import { tokens } from "@/design-system/tokens";

// ── helpers ───────────────────────────────────────────────

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return mobile;
}

// ── step data ─────────────────────────────────────────────
const STEPS = [
  {
    Icon: User,
    label: "PROFILE",
    title: "Your artist profile",
    desc: "The basics. Two minutes, then everything else gets easier.",
    options: [
      { Icon: PenLine,  text: "Artist or band name",                                        route: "/settings?tab=profile" },
      { Icon: Music2,   text: "Instrument(s) you play",                                     route: "/settings?tab=profile" },
      { Icon: AtSign,   text: "Social handles", sub: "Instagram, X, TikTok, Bluesky",       route: "/settings?tab=profile" },
    ],
  },
  {
    Icon: CalendarDays,
    label: "GIGS",
    title: "Get your gigs in",
    desc: "Fretless sits on top of your calendar so you can see the rest of life around the music.",
    options: [
      { Icon: Globe,     text: "Connect Google Calendar", sub: "Pick your gig calendar. Gigs sync there, personal events stay out", route: "/settings?tab=integrations" },
      { Icon: Plus,      text: "Add a gig manually",                                                   route: "/gigs/new" },
      { Icon: Clipboard, text: "Paste a gig text or email", sub: "Smart Fill reads the details for you", route: "/gigs/new" },
    ],
  },
  {
    Icon: Users,
    label: "PEOPLE",
    title: "Your people",
    desc: "Bandmates, deps, MDs, fixers. Pull them in once, tag them anywhere.",
    options: [
      { Icon: Users,    text: "Import from contacts",            sub: "iOS and Android",             route: "/scene" },
      { Icon: FileText, text: "Import from accounting software", sub: "FreeAgent, Xero, QuickBooks", route: "/settings?tab=integrations" },
      { Icon: UserPlus, text: "Add manually",                                                        route: "/scene" },
    ],
  },
  {
    Icon: MapPin,
    label: "SCENE",
    title: "Your scene",
    desc: "Everyone and everywhere you've performed — building quietly in the background.",
    options: [
      { Icon: Building2, text: "Venues you've played",   route: "/scene" },
      { Icon: Mic,       text: "Artists and band names", route: "/scene" },
      { Icon: Users,     text: "Fellow performers",      route: "/scene" },
    ],
  },
  {
    Icon: Navigation,
    label: "GIG BRIEF",
    title: "Your Gig Brief",
    desc: "Everything you need before you leave the house, recalculated in real time.",
    highlight: { Icon: Clock, title: "Leave by 14:45", sub: "Live travel · 45 min + 30 min buffer" },
    options: [
      { Icon: Map,        text: "Open in Waze or Citymapper", route: "/dashboard" },
      { Icon: CreditCard, text: "Fee, dress code, call time", route: "/dashboard" },
      { Icon: Share2,     text: "Share with the band",        route: "/dashboard" },
    ],
  },
  {
    Icon: LayoutDashboard,
    label: "DASHBOARD",
    title: "Your dashboard",
    desc: "Make it yours. Drag the modules you want, hide the ones you don't.",
    options: [
      { Icon: GripVertical, text: "Drag to reorder",          route: "/dashboard" },
      { Icon: EyeOff,       text: "Hide what you don't need", route: "/dashboard" },
    ],
  },
  {
    Icon: Sparkles,
    label: "PROMO",
    title: "Tell the world",
    desc: "AI promo posts written in your voice, scheduled for when your audience is actually online.",
    options: [
      { Icon: Sparkles, text: "AI promo posts",              route: "/social" },
      { Icon: Hash,     text: "Your hashtags and style",     route: "/social" },
      { Icon: Bell,     text: "Best time to post reminders", route: "/social" },
    ],
  },
  {
    Icon: Globe,
    label: "WEBSITE",
    title: "Update your website",
    desc: "Push public gigs out to listings sites, or export a CSV for anything else.",
    options: [
      { Icon: Download, text: "Bandsintown CSV",           sub: "Ready-made format", route: "/calendar" },
      { Icon: Download, text: "Songkick CSV",              sub: "Ready-made format", route: "/calendar" },
      { Icon: FileText, text: "Generic CSV for your site",                           route: "/calendar" },
    ],
  },
  {
    Icon: Receipt,
    label: "INVOICE",
    title: "Invoice",
    desc: "One tap from a gig card to a sent invoice. Never go searching for the info again.",
    options: [
      { Icon: FileText, text: "Invoice from gig card",                 route: "/invoices" },
      { Icon: Globe,    text: "Push to FreeAgent, Xero or QuickBooks", route: "/invoices" },
      { Icon: Home,     text: "Set up invoice details in Settings",     route: "/invoices/settings" },
    ],
  },
  {
    Icon: Music,
    label: "READY",
    title: "Go and play",
    desc: "",
    finish: true,
  },
];

// ── sub-components ────────────────────────────────────────

function StepDots({ current, total, onJump }) {
  return (
    <div style={{ display: "flex", gap: tokens.space.xs, justifyContent: "center", alignItems: "center" }}>
      {Array.from({ length: total }).map((_, i) => {
        const active = i === current;
        const done   = i < current;
        return (
          <button
            key={i}
            onClick={() => onJump(i)}
            aria-label={`Go to step ${i + 1}`}
            style={{
              width:        active ? 22 : 5,
              height:       5,
              borderRadius: active ? tokens.radius.sm : tokens.radius.pill,
              background:   active ? tokens.color.orange : done ? tokens.color.orangeLine : tokens.color.innerBorder,
              border:       "none",
              padding:      0,
              cursor:       "pointer",
              transition:   `width 280ms cubic-bezier(.4,0,.2,1), background ${tokens.motion.slow}`,
              flexShrink:   0,
            }}
          />
        );
      })}
    </div>
  );
}

function OptionRow({ Icon: Ico, text, sub, checked, onNavigate, onCheck }) {
  return (
    <div
      onClick={onNavigate}
      style={{
        background:   tokens.color.innerBg,
        border:       tokens.border.inner,
        borderRadius: tokens.radius.lg,
        display:      "flex",
        alignItems:   "center",
        gap:          tokens.space.md,
        padding:      `${tokens.space.md}px ${tokens.space.lg}px`,
        cursor:       "pointer",
        transition:   `border-color ${tokens.motion.fast}, background ${tokens.motion.fast}`,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = tokens.color.innerBorder; }}
      onMouseLeave={e => { e.currentTarget.style.background = tokens.color.innerBg; }}
    >
      {/* checkbox */}
      <button
        onClick={e => { e.stopPropagation(); onCheck(); }}
        aria-label={checked ? "Mark incomplete" : "Mark complete"}
        style={{
          flexShrink:     0,
          width:          22,
          height:         22,
          borderRadius:   tokens.radius.sm,
          background:     checked ? tokens.color.orange : "transparent",
          border:         checked ? `2px solid ${tokens.color.orange}` : tokens.border.inner,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          cursor:         "pointer",
          padding:        0,
          transition:     `background ${tokens.motion.base}, border-color ${tokens.motion.base}`,
        }}
      >
        {checked && <Check size={13} color={tokens.color.cardBg} strokeWidth={3} aria-hidden="true" />}
      </button>

      {/* icon badge */}
      <div
        style={{
          width:          32,
          height:         32,
          borderRadius:   tokens.radius.md,
          flexShrink:     0,
          background:     tokens.color.orangeSoft,
          border:         tokens.border.brandSoft,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
        }}
      >
        <Ico size={16} color={tokens.color.orange} aria-hidden="true" />
      </div>

      {/* text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...tokens.type.body, color: tokens.color.text, lineHeight: 1.25 }}>
          {text}
        </div>
        {sub && (
          <div style={{ ...tokens.type.bodySm, color: tokens.color.textMuted, marginTop: 2 }}>
            {sub}
          </div>
        )}
      </div>

      <ChevronRight size={15} color={tokens.color.textMuted} aria-hidden="true" style={{ flexShrink: 0 }} />
    </div>
  );
}

function HighlightCard({ Icon: Ico, title, sub }) {
  return (
    <div
      style={{
        background:   tokens.color.orangeSoft,
        border:       tokens.border.brandSoft,
        borderRadius: tokens.radius.xl,
        padding:      `${tokens.space.lg}px ${tokens.space.xl}px`,
        display:      "flex",
        alignItems:   "center",
        gap:          tokens.space.lg,
      }}
    >
      <div
        style={{
          width:          44,
          height:         44,
          borderRadius:   tokens.radius.lg,
          flexShrink:     0,
          background:     "rgba(245,137,31,0.18)",
          border:         tokens.border.brandSoft,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
        }}
      >
        <Ico size={22} color={tokens.color.orange} aria-hidden="true" />
      </div>
      <div>
        <div style={{ ...tokens.type.titleSm, color: tokens.color.orange }}>{title}</div>
        <div style={{ ...tokens.type.bodySm, color: tokens.color.textDim, marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}

function FinishCard() {
  return (
    <div
      style={{
        flex:           1,
        display:        "flex",
        flexDirection:  "column",
        alignItems:     "center",
        justifyContent: "center",
        textAlign:      "center",
        padding:        `${tokens.space.md}px ${tokens.space.sm}px ${tokens.space.xxl}px`,
      }}
    >
      {/* Wordmark with ambient glow */}
      <div
        style={{
          position:       "relative",
          marginBottom:   tokens.space.xxxl,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          width:          "100%",
        }}
      >
        <div
          style={{
            position:      "absolute",
            width:         260,
            height:        260,
            borderRadius:  "50%",
            background:    `radial-gradient(circle, ${tokens.color.orangeSoft} 0%, rgba(245,137,31,0.04) 45%, rgba(245,137,31,0) 70%)`,
            pointerEvents: "none",
            filter:        "blur(2px)",
          }}
        />
        <div
          style={{
            position:      "relative",
            fontFamily:    tokens.font.wordmark,
            fontWeight:    800,
            fontSize:      52,
            letterSpacing: "-0.025em",
            lineHeight:    1,
          }}
        >
          <span style={{ color: tokens.color.text }}>Fret</span>
          <span style={{ color: tokens.color.orange }}>less</span>
        </div>
      </div>

      {/* Kicker */}
      <div style={{ ...tokens.type.kicker, color: tokens.color.orange, marginBottom: tokens.space.md }}>
        You&rsquo;re all set
      </div>

      {/* Hero title */}
      <div
        style={{
          ...tokens.type.heroSm,
          color:        tokens.color.text,
          marginBottom: tokens.space.lg,
          maxWidth:     280,
        }}
      >
        Go and play.
      </div>

      {/* Body */}
      <div style={{ ...tokens.type.body, color: tokens.color.textDim, maxWidth: 290 }}>
        Now you&rsquo;ve saved yourself hours of admin &mdash; go play and live a Fretless life.
      </div>
    </div>
  );
}

function Slide({ step, stepIndex, rowsComplete, onRowCheck, onNavAndClose }) {
  if (step.finish) return <FinishCard />;

  const { Icon: Ico, label, title, desc, highlight, options } = step;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Icon circle */}
      <div
        style={{
          width:          52,
          height:         52,
          borderRadius:   tokens.radius.pill,
          background:     tokens.color.orangeSoft,
          border:         tokens.border.brandSoft,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          marginBottom:   tokens.space.lg,
          flexShrink:     0,
        }}
      >
        <Ico size={24} color={tokens.color.orange} aria-hidden="true" />
      </div>

      {/* Kicker label */}
      <div style={{ ...tokens.type.kickerSm, color: tokens.color.orange, marginBottom: tokens.space.sm }}>
        {label}
      </div>

      {/* Title */}
      <div
        style={{
          fontFamily:    tokens.font.body,
          fontWeight:    600,
          fontSize:      26,
          letterSpacing: "-0.025em",
          lineHeight:    1.08,
          color:         tokens.color.text,
          marginBottom:  tokens.space.sm,
        }}
      >
        {title}
      </div>

      {/* Description */}
      {desc && (
        <div style={{ ...tokens.type.body, color: tokens.color.textDim, marginBottom: tokens.space.xl, maxWidth: 320 }}>
          {desc}
        </div>
      )}

      {/* Highlight */}
      {highlight && (
        <div style={{ marginBottom: tokens.space.md }}>
          <HighlightCard {...highlight} />
        </div>
      )}

      {/* Options */}
      {options && (
        <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.sm }}>
          {options.map((o, rowIdx) => (
            <OptionRow
              key={rowIdx}
              Icon={o.Icon}
              text={o.text}
              sub={o.sub}
              checked={!!(rowsComplete[stepIndex] && rowsComplete[stepIndex][rowIdx])}
              onNavigate={() => onNavAndClose(o.route)}
              onCheck={() => onRowCheck(stepIndex, rowIdx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── main export ───────────────────────────────────────────

export default function OnboardingCarousel({ stepsComplete, onToggle, onDismissAll }) {
  const { setUser, user } = useAuth();
  // Prefer live AuthContext data over the prop snapshot for initialisation
  const savedSteps = user?.onboarding_steps_complete || stepsComplete || Array(10).fill(false);
  const safe = savedSteps;

  // Row-level completion: bool[stepIndex][rowIndex]
  // Initialise from the user's saved step completions — all rows of a
  // completed step start ticked; rows of an incomplete step start unticked.
  const [rowsComplete, setRowsComplete] = useState(() =>
    STEPS.map((step, si) =>
      (step.options || []).map(() => !!savedSteps[si])
    )
  );

  const [stepIndex, setStepIndex] = useState(() => {
    const first = safe.findIndex(v => !v);
    return first === -1 ? 0 : first;
  });
  const [minimised, setMinimised] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const navigate    = useNavigate();
  const isMobile    = useIsMobile();

  const doneCount = (user?.onboarding_steps_complete || safe).filter(Boolean).length;
  const pct       = (doneCount / STEPS.length) * 100;

  const step     = STEPS[stepIndex];
  const total    = STEPS.length;
  const isFinish = !!step.finish;

  const persistStep = useCallback(async (idx) => {
    try {
      await apiClient.patch("/user/profile", { onboarding_step: idx });
    } catch {}
  }, []);

  const go = useCallback(async (next) => {
    if (next < 0 || next >= total) return;
    setStepIndex(next);
    await persistStep(next);
  }, [total, persistStep]);

  // Toggle a single option row and immediately persist the full step
  // completion array so every tick is saved, not just when a step finishes.
  const handleRowCheck = useCallback((stepIdx, rowIdx) => {
    setRowsComplete(prev => {
      const nextRows = prev.map((rows, si) =>
        si === stepIdx
          ? rows.map((v, ri) => ri === rowIdx ? !v : v)
          : rows
      );
      // Derive step-level completions from the updated row state and PATCH now
      const nextStepsComplete = STEPS.map((step, si) =>
        step.options?.length ? nextRows[si].every(Boolean) : !!safe[si]
      );
      apiClient.patch("/user/profile", { onboarding_steps_complete: nextStepsComplete }).catch(() => {});
      // Also notify parent for optimistic UI update (doneCount, minimised bar)
      const allRowsDone = nextRows[stepIdx].every(Boolean);
      if (allRowsDone) {
        onToggle?.(stepIdx);
      }
      return nextRows;
    });
  }, [safe, onToggle]);

  const handleNavAndClose = useCallback((route) => {
    // Minimise first so the overlay is gone before the new page renders,
    // then navigate after one frame to let the state update flush.
    setMinimised(true);
    setTimeout(() => navigate(route), 50);
  }, [navigate]);

  const handleReset = useCallback(async () => {
    const blank = Array(total).fill(false);
    setRowsComplete(STEPS.map(s => (s.options || []).map(() => false)));
    setUser(prev => prev ? { ...prev, onboarding_steps_complete: blank, onboarding_complete: false } : prev);
    apiClient.patch("/user/profile", { onboarding_steps_complete: blank, onboarding_complete: false }).catch(() => {});
    go(0);
  }, [total, setUser, go]);


  // ── minimised bar ─────────────────────────────────────
  if (minimised) {
    return (
      <div
        onClick={() => setMinimised(false)}
        role="button"
        aria-label={`Getting Started: ${doneCount} of ${STEPS.length} complete. Tap to expand.`}
        style={{
          position:      "fixed",
          top:           0,
          left:          0,
          right:         0,
          zIndex:        99999,
          height:        "calc(52px + env(safe-area-inset-top, 0px))",
          paddingTop:    "env(safe-area-inset-top, 0px)",
          background:    tokens.color.orange,
          cursor:        "pointer",
          fontFamily:    tokens.font.body,
          WebkitFontSmoothing: "antialiased",
          display:       "flex",
          flexDirection: "column",
        }}
      >
        {/* text row — centred, fills the 52px */}
        <div
          style={{
            flex:           1,
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            gap:            tokens.space.xs,
            padding:        `0 44px`,
            position:       "relative",
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); onDismissAll && onDismissAll(); }}
            aria-label="Dismiss onboarding"
            style={{
              background: "none",
              border:     "none",
              color:      "rgba(0,0,0,0.55)",
              fontSize:   20,
              cursor:     "pointer",
              padding:    `${tokens.space.xxs}px ${tokens.space.sm}px`,
              position:   "absolute",
              left:       tokens.space.lg,
            }}
          >
            ×
          </button>
          <span style={{ ...tokens.type.button, color: tokens.color.onOrange }}>
            Getting Started
          </span>
          <span style={{ ...tokens.type.button, color: "rgba(0,0,0,0.5)", fontWeight: 500 }}>
            · {doneCount}/{STEPS.length} complete
          </span>
          <ChevronDown
            size={20}
            color="rgba(0,0,0,0.45)"
            aria-hidden="true"
            style={{ position: "absolute", right: tokens.space.lg }}
          />
        </div>
        {/* progress bar — darker strip at the very bottom */}
        <div style={{ height: 3, background: "rgba(0,0,0,0.15)" }}>
          <div
            style={{
              height:     "100%",
              width:      `${pct}%`,
              background: "rgba(0,0,0,0.32)",
              transition: `width 350ms cubic-bezier(.4,0,.2,1)`,
            }}
          />
        </div>
      </div>
    );
  }

  // ── full carousel ──────────────────────────────────────
  // Mobile: full-screen solid overlay.
  // Desktop: dark scrim with a centred fixed-height card — all slides same size.
  return (
    <>
    <style>{`
  .onboarding-nav {
    padding-top: ${tokens.space.lg}px;
    padding-left: ${tokens.space.xl}px;
    padding-right: ${tokens.space.xl}px;
    padding-bottom: max(${tokens.space.lg}px, calc(env(safe-area-inset-bottom) + ${tokens.space.lg}px));
  }
`}</style>
    <div
      style={{
        position:        "fixed",
        top:             0,
        left:            0,
        right:           0,
        bottom:          "calc(60px + env(safe-area-inset-bottom, 0px))",
        zIndex:          9000,
        display:         "flex",
        alignItems:      isMobile ? "stretch" : "center",
        justifyContent:  isMobile ? "stretch" : "center",
        background:      isMobile ? tokens.color.cardBg : "rgba(0,0,0,0.72)",
        fontFamily:      tokens.font.body,
        WebkitFontSmoothing: "antialiased",
      }}
    >
    <div
      onTouchStart={e => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
      }}
      onTouchEnd={e => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        const dy = e.changedTouches[0].clientY - touchStartY.current;
        touchStartX.current = null;
        touchStartY.current = null;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
        if (dx < 0) go(stepIndex + 1);
        else go(stepIndex - 1);
      }}
      style={{
        display:       "flex",
        flexDirection: "column",
        background:    tokens.color.cardBg,
        color:         tokens.color.text,
        paddingTop:    "env(safe-area-inset-top, 16px)",
        ...(isMobile
          ? { flex: 1, paddingBottom: tokens.space.lg }
          : {
              width:        "100%",
              maxWidth:     480,
              height:       "min(680px, 90vh)",   // fixed → every slide identical height
              borderRadius: tokens.radius.xxl,
              border:       tokens.border.card,
              boxShadow:    tokens.shadow.cardLg,
            }
        ),
      }}
    >
      {/* Header */}
      <div
        style={{
          display:        "flex",
          justifyContent: "space-between",
          alignItems:     "center",
          padding:        `${tokens.space.lg}px ${tokens.space.xl}px 0`,
          flexShrink:     0,
        }}
      >
        <div style={{ ...tokens.type.wordmark, color: tokens.color.text }}>
          Fret<span style={{ color: tokens.color.orange }}>less</span>
        </div>
        <button
          onClick={() => {
            apiClient.patch("/user/profile", { onboarding_complete: true }).catch(() => {});
            onDismissAll && onDismissAll();
          }}
          style={{
            ...tokens.type.button,
            background: "none",
            border:     "none",
            color:      tokens.color.textMuted,
            cursor:     "pointer",
            padding:    `${tokens.space.xxs}px ${tokens.space.sm}px`,
          }}
        >
          Skip
        </button>
      </div>

      {/* Progress dots */}
      <div style={{ padding: `${tokens.space.lg}px ${tokens.space.xl}px ${tokens.space.md}px`, flexShrink: 0 }}>
        <StepDots current={stepIndex} total={total} onJump={go} />
      </div>

      {/* Slide content — flex: 1 fills remaining fixed height */}
      <div
        style={{
          flex:          1,
          padding:       `0 ${tokens.space.xl}px`,
          display:       "flex",
          flexDirection: "column",
          minHeight:     0,
          overflowY:     "auto",
        }}
      >
        <Slide
          step={step}
          stepIndex={stepIndex}
          rowsComplete={rowsComplete}
          onRowCheck={handleRowCheck}
          onNavAndClose={handleNavAndClose}
        />
      </div>

      {/* Navigation bar */}
      <div
        className="onboarding-nav"
        style={{
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          gap:            tokens.space.md,
          flexShrink:     0,
        }}
      >
        <button
          onClick={() => go(stepIndex - 1)}
          disabled={stepIndex === 0}
          style={{
            ...tokens.type.button,
            background: "transparent",
            border:     "none",
            color:      stepIndex === 0 ? "transparent" : tokens.color.textMuted,
            cursor:     stepIndex === 0 ? "default" : "pointer",
            display:    "flex",
            alignItems: "center",
            gap:        tokens.space.xxs,
            padding:    `${tokens.space.sm}px ${tokens.space.xxs}px`,
          }}
          aria-label="Previous step"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back
        </button>

        <button
          onClick={() => setMinimised(true)}
          aria-label="Minimise onboarding"
          style={{
            width:          44,
            height:         44,
            borderRadius:   tokens.radius.pill,
            background:     tokens.color.orangeSoft,
            border:         tokens.border.brandSoft,
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            cursor:         "pointer",
            lineHeight:     0,
            flexShrink:     0,
          }}
        >
          <ChevronUp size={22} color={tokens.color.orange} aria-hidden="true" />
        </button>

        {!isFinish && (
          <button
            onClick={() => go(stepIndex + 1)}
            style={{
              ...tokens.type.button,
              background:   tokens.color.orange,
              border:       "none",
              color:        tokens.color.onOrange,
              borderRadius: tokens.radius.pill,
              padding:      `11px 22px`,
              cursor:       "pointer",
              display:      "flex",
              alignItems:   "center",
              gap:          tokens.space.xs,
              boxShadow:    `0 6px 20px ${tokens.color.orangeSoft}`,
              whiteSpace:   "nowrap",
            }}
            aria-label="Next step"
          >
            Next
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        )}
        {isFinish && <div style={{ width: 44 }} />}
      </div>

      {/* Reset link — final slide only */}
      {isFinish && (
        <div style={{ textAlign: "center", paddingBottom: tokens.space.sm }}>
          <button
            onClick={handleReset}
            style={{
              ...tokens.type.bodySm,
              background:     "none",
              border:         "none",
              color:          tokens.color.textMuted,
              cursor:         "pointer",
              textDecoration: "underline",
              padding:        `${tokens.space.xxs}px ${tokens.space.sm}px`,
            }}
          >
            Reset tutorial
          </button>
        </div>
      )}
    </div>
    </div>
    </>
  );
}
